import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, Plus } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { decimalOpcional } from "@/lib/decimales"
import { usarSesion } from "@/lib/sesion"

export type TipoGasto = "FIJO" | "VARIABLE"

/** Una partida del plan de cuentas, traído del 2.0 (28/10). */
export type CuentaContable = {
  id: number
  codigo: string
  descripcion: string
  tipo: string
  activo: boolean
  etiqueta: string
}

export type CategoriaGasto = {
  id: number
  nombre: string
  descripcion: string | null
  tipo_gasto: TipoGasto
  activo: boolean
  /** La partida del plan de cuentas (28/10). Opcional en el tipo: hay pruebas sin ella. */
  cuenta_id?: number | null
  cuenta?: CuentaContable | null
}

export type Gasto = {
  id: number
  codigo: string
  nombre: string
  descripcion: string | null
  categoria_id: number | null
  categoria: string | null
  proveedor_id: number | null
  proveedor: string | null
  /** O el tipo de proveedor que lo presta (04/11): «Almacenaje» de las almacenadoras. */
  tipo_proveedor_id?: number | null
  tipo_proveedor?: string | null
  tipo_gasto: TipoGasto
  monto_referencia: string | null
  divisa_id: number | null
  divisa: string | null
  activo: boolean
}

type Proveedor = { id: number; nombre: string; rif: string | null }
type Divisa = { id: number; codigo: string; nombre: string }

/**
 * Crea una categoría de gasto y devuelve su `id`. La usan los selectores que dejan
 * crear sin salir del formulario: aquí y en las órdenes de pago.
 */
export async function crearCategoria(nombre: string, tipo: TipoGasto): Promise<number> {
  const creada = await api.post<CategoriaGasto>("/api/v1/categorias-gasto", {
    nombre,
    tipo_gasto: tipo,
  })
  return creada.id
}

/**
 * Catálogo de gastos administrativos: en qué gasta la agencia y de qué tipo es. Vive
 * como pestaña del Catálogo, que pone el título.
 *
 * ⚠ **Esto no es el gasto de un expediente** —ese es un rubro— ni compromete dinero
 * con nadie: es la plantilla desde la que se redacta una orden de pago.
 *
 * ⚠ **El importador no ve esta pantalla, y no porque el enlace esté escondido.** El
 * servidor responde 404 a un usuario `CLIENTE` antes de mirar el permiso
 * (`requiere_interno`): el gasto de la agencia es su margen.
 *
 * ⚠ **La categoría se crea desde su propio desplegable.** Antes había que salir a
 * otra pantalla —que además no existía— y el gasto se guardaba «Sin categoría»; así es
 * como un catálogo se llena de huecos que ningún informe sabe agrupar. La nueva nace
 * con el tipo que tenga elegido el gasto, que es lo más probable.
 */
export type FiltroGasto = "fijos" | "variables" | "sin-categoria" | "archivados" | null

/** Qué deja pasar cada filtro. Es lo que convierte una tarjeta en una pregunta útil. */
const PASA: Record<string, (g: Gasto) => boolean> = {
  fijos: (g) => g.activo && g.tipo_gasto === "FIJO",
  variables: (g) => g.activo && g.tipo_gasto === "VARIABLE",
  "sin-categoria": (g) => g.activo && g.categoria_id === null,
  archivados: (g) => !g.activo,
}

export function Gastos({ filtro = null }: { filtro?: FiltroGasto } = {}) {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const [abierto, setAbierto] = useState(false)
  const [enEdicion, setEnEdicion] = useState<Gasto | null>(null)

  const puedeAdministrar = tienePermiso(usuario, "gasto:administrar")

  const gastos = useQuery({
    queryKey: ["gastos"],
    queryFn: () => api.get<Gasto[]>("/api/v1/gastos?limit=100"),
  })

  function abrir(gasto: Gasto | null) {
    setEnEdicion(gasto)
    setAbierto(true)
  }

  if (gastos.isPending) {
    return <p className="py-6">Cargando…</p>
  }
  if (gastos.isError) {
    return (
      <p className="flex items-center gap-2 py-6" style={{ color: "var(--destructive)" }}>
        <AlertCircle size={16} /> No se pudo cargar el catálogo de gastos.
      </p>
    )
  }

  // ⚠ **El filtro lo manda la tarjeta de arriba.** Una cifra que no se puede pulsar
  // obliga a recorrer la tabla a ojo para ver cuáles son, que es justo lo que la
  // tarjeta venía a ahorrar.
  const pasa = filtro === null ? null : PASA[filtro]
  const filas = pasa === undefined || pasa === null ? gastos.data : gastos.data.filter(pasa)

  return (
    <section className="space-y-4">
      {puedeAdministrar && (
        <div className="flex justify-end">
          <Boton variante="primario" onClick={() => abrir(null)}>
            <Plus size={16} /> Nuevo gasto
          </Boton>
        </div>
      )}

      <Tabla etiqueta="Catálogo de gastos administrativos">
        <Cabecera>
          <Th>Código</Th>
          <Th>Gasto</Th>
          <Th>Categoría</Th>
          <Th>Proveedor</Th>
          <Th>Tipo</Th>
          <Th derecha>Referencia</Th>
          <Th>Estado</Th>
        </Cabecera>
        <Cuerpo>
          {filas.length === 0 && (
            <TablaVacia colSpan={7}>
              Todavía no hay gastos en el catálogo. El primero suele ser el alquiler.
            </TablaVacia>
          )}
          {filas.map((gasto) => (
            <Fila
              key={gasto.id}
              atenuada={!gasto.activo}
              className={puedeAdministrar ? "cursor-pointer" : undefined}
              onClick={puedeAdministrar ? () => abrir(gasto) : undefined}
            >
              <Td>{gasto.codigo}</Td>
              <Td>{gasto.nombre}</Td>
              <Td>{gasto.categoria ?? "—"}</Td>
              <Td>
                {gasto.proveedor ??
                  (gasto.tipo_proveedor ? `Los de tipo ${gasto.tipo_proveedor}` : "—")}
              </Td>
              <Td>
                <Insignia tono={gasto.tipo_gasto === "FIJO" ? "info" : "neutro"}>
                  {gasto.tipo_gasto === "FIJO" ? "Fijo" : "Variable"}
                </Insignia>
              </Td>
              <Td
                derecha
                numero
                // Al exportar, la cifra en formato venezolano con su moneda delante: así
                // sale como número si toda la columna es de una moneda.
                data-exportar-valor={
                  gasto.monto_referencia
                    ? `${gasto.divisa ?? ""} ${monto(gasto.monto_referencia)}`.trim()
                    : "—"
                }
              >
                {gasto.monto_referencia ? `${gasto.monto_referencia} ${gasto.divisa ?? ""}` : "—"}
              </Td>
              <Td>
                {gasto.activo ? (
                  <Insignia tono="exito">Activo</Insignia>
                ) : (
                  <Insignia tono="neutro">Archivado</Insignia>
                )}
              </Td>
            </Fila>
          ))}
        </Cuerpo>
      </Tabla>

      <Modal
        abierto={abierto}
        titulo={enEdicion ? `Editar ${enEdicion.codigo}` : "Nuevo gasto"}
        descripcion="Los datos del gasto son una referencia: la orden de pago lleva su propio monto."
        onCerrar={() => setAbierto(false)}
      >
        {/* La `key` vuelve a montar el formulario con cada gasto: sin ella, abrir otro
            conservaría lo elegido en los selectores del anterior. */}
        {abierto && (
          <FormularioGasto
            key={enEdicion?.id ?? "nuevo"}
            gasto={enEdicion}
            onCerrar={() => setAbierto(false)}
            onGuardado={() => {
              clienteQuery.invalidateQueries({ queryKey: ["gastos"] })
              setAbierto(false)
            }}
          />
        )}
      </Modal>
    </section>
  )
}

/**
 * Alta o edición de un gasto del catálogo.
 *
 * ⚠ **Con `proveedorFijo` es «un servicio de ESTE proveedor»** (04/11): lo abre la ficha
 * del proveedor, que ya dice de quién es, así que no se ofrece el proveedor ni se pide
 * código —el servidor asigna `GAS-0001`—. Es el mismo catálogo y el mismo formulario: un
 * segundo formulario para lo mismo se desincroniza al primer campo nuevo.
 *
 * ⚠ **Con `tipoFijo` es «un servicio del TIPO»** (04/11): «Almacenaje» de «Almacenadora»,
 * que cada almacenadora marca en su ficha con su precio. Aquí el monto es el de
 * REFERENCIA: el de quien no tenga uno propio. Un servicio es de un proveedor o de un
 * tipo, nunca de los dos (lo respalda un `CHECK`), y en el catálogo general se elige uno
 * u otro.
 */
export function FormularioGasto({
  gasto,
  proveedorFijo,
  tipoFijo,
  onCerrar,
  onGuardado,
}: {
  gasto: Gasto | null
  proveedorFijo?: { id: number; nombre: string }
  tipoFijo?: { id: number; nombre: string }
  onCerrar: () => void
  onGuardado: () => void
}) {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()

  const [categoriaId, setCategoriaId] = useState<number | null>(gasto?.categoria_id ?? null)
  const [proveedorId, setProveedorId] = useState<number | null>(
    gasto?.proveedor_id ?? proveedorFijo?.id ?? null,
  )
  const [tipoProveedorId, setTipoProveedorId] = useState<number | null>(
    gasto?.tipo_proveedor_id ?? tipoFijo?.id ?? null,
  )
  const fijo = proveedorFijo !== undefined || tipoFijo !== undefined
  const [divisaId, setDivisaId] = useState<number | null>(gasto?.divisa_id ?? null)
  const [tipo, setTipo] = useState<TipoGasto>(gasto?.tipo_gasto ?? "VARIABLE")

  const categorias = useQuery({
    queryKey: ["categorias-gasto"],
    queryFn: () => api.get<CategoriaGasto[]>("/api/v1/categorias-gasto"),
  })
  // Administrativos Y operativos (04/11): el catálogo es lo que ofrece cada proveedor.
  const elegirProveedor = !fijo && tienePermiso(usuario, "proveedor:ver")
  const tiposProveedor = useQuery({
    queryKey: ["tipos-proveedor"],
    queryFn: () =>
      api.get<{ id: number; nombre: string; activo: boolean }[]>("/api/v1/tipos-proveedor"),
    enabled: elegirProveedor,
  })
  const administrativos = useQuery({
    queryKey: ["proveedores", "ADMINISTRATIVO"],
    queryFn: () =>
      api.get<Proveedor[]>("/api/v1/proveedores?limit=100&activo=true&categoria=ADMINISTRATIVO"),
    enabled: elegirProveedor,
  })
  const operativos = useQuery({
    queryKey: ["proveedores", "OPERATIVO", "catalogo-gastos"],
    queryFn: () =>
      api.get<Proveedor[]>("/api/v1/proveedores?limit=100&activo=true&categoria=OPERATIVO"),
    enabled: elegirProveedor,
  })
  const proveedores = [
    ...new Map(
      [...(administrativos.data ?? []), ...(operativos.data ?? [])].map((p) => [p.id, p]),
    ).values(),
  ].sort((a, b) => a.nombre.localeCompare(b.nombre, "es"))
  const divisas = useQuery({
    queryKey: ["divisas"],
    queryFn: () => api.get<Divisa[]>("/api/v1/divisas"),
    // La misma lectura que abre la ruta: tesorería o quien redacta órdenes.
    enabled: tienePermiso(usuario, "tesoreria:ver") || tienePermiso(usuario, "orden_pago:crear"),
  })

  const guardar = useMutation({
    mutationFn: (cuerpo: Record<string, unknown>) =>
      gasto ? api.patch(`/api/v1/gastos/${gasto.id}`, cuerpo) : api.post("/api/v1/gastos", cuerpo),
    onSuccess: onGuardado,
  })

  function enviar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    const datos = new FormData(evento.currentTarget)
    const texto = (campo: string) => {
      const valor = String(datos.get(campo) ?? "").trim()
      return valor === "" ? null : valor
    }
    const cuerpo: Record<string, unknown> = {
      nombre: texto("nombre"),
      descripcion: texto("descripcion"),
      categoria_id: categoriaId,
      proveedor_id: proveedorId,
      tipo_proveedor_id: tipoProveedorId,
      tipo_gasto: tipo,
      monto_referencia: decimalOpcional(texto("monto_referencia")),
      divisa_id: divisaId,
    }
    // El código es la identidad del gasto: se teclea al crearlo y ya no se cambia,
    // porque aparece impreso en las órdenes que ya salieron. Desde la ficha del
    // proveedor no se pide: lo asigna el servidor.
    if (!gasto && !fijo) cuerpo.codigo = texto("codigo")
    guardar.mutate(cuerpo)
  }

  const error = guardar.error instanceof ErrorApi ? guardar.error.message : null

  return (
    <form className="space-y-4" onSubmit={enviar}>
      {!gasto && !fijo && (
        <Campo para="gasto-codigo" etiqueta="Código" ayuda="Corto y estable: ALQ-OFI, INT-FIBRA.">
          <input id="gasto-codigo" name="codigo" required maxLength={30} className="campo mt-1" />
        </Campo>
      )}
      <Campo para="gasto-nombre" etiqueta="Nombre">
        <input
          id="gasto-nombre"
          name="nombre"
          required
          maxLength={200}
          defaultValue={gasto?.nombre ?? ""}
          className="campo mt-1"
        />
      </Campo>
      <Campo para="gasto-descripcion" etiqueta="Descripción">
        <input
          id="gasto-descripcion"
          name="descripcion"
          maxLength={300}
          defaultValue={gasto?.descripcion ?? ""}
          className="campo mt-1"
        />
      </Campo>
      <div className="grid gap-4 sm:grid-cols-2">
        <Campo
          para="gasto-categoria"
          etiqueta="Categoría"
          ayuda="Escribe para buscar; si no existe, créala desde aquí."
        >
          <SelectorBuscable
            id="gasto-categoria"
            opciones={(categorias.data ?? [])
              .filter((c) => c.activo || c.id === categoriaId)
              .map((c) => ({
                valor: c.id,
                etiqueta: c.nombre,
                detalle: c.tipo_gasto === "FIJO" ? "Fijo" : "Variable",
              }))}
            valor={categoriaId}
            onCambiar={setCategoriaId}
            vacio="Sin categoría"
            queSeCrea="categoría"
            onCrear={
              tienePermiso(usuario, "gasto:administrar")
                ? async (nombre) => {
                    const id = await crearCategoria(nombre, tipo)
                    await clienteQuery.invalidateQueries({ queryKey: ["categorias-gasto"] })
                    return id
                  }
                : undefined
            }
          />
        </Campo>
        <Campo para="gasto-tipo" etiqueta="Tipo">
          <select
            id="gasto-tipo"
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoGasto)}
            className="campo mt-1"
          >
            <option value="FIJO">Fijo</option>
            <option value="VARIABLE">Variable</option>
          </select>
        </Campo>
        {!fijo && (
          <Campo
            para="gasto-tipo-proveedor"
            etiqueta="Tipo de proveedor"
            ayuda="Si lo prestan varios: cada uno lo marca en su ficha con su precio."
          >
            <SelectorBuscable
              id="gasto-tipo-proveedor"
              opciones={(tiposProveedor.data ?? [])
                .filter((t) => t.activo || t.id === tipoProveedorId)
                .map((t) => ({ valor: t.id, etiqueta: t.nombre }))}
              valor={tipoProveedorId}
              onCambiar={(id) => {
                setTipoProveedorId(id)
                // De un tipo O de un proveedor: elegir uno suelta el otro.
                if (id !== null) setProveedorId(null)
              }}
              vacio="De ningún tipo"
            />
          </Campo>
        )}
        {!fijo && (
          <Campo
            para="gasto-proveedor"
            etiqueta="Proveedor habitual"
            ayuda="El que lo ofrece. A uno operativo se le paga con una orden vinculada a un expediente."
          >
            <SelectorBuscable
              id="gasto-proveedor"
              opciones={proveedores.map((p) => ({
                valor: p.id,
                etiqueta: p.nombre,
                detalle: p.rif ?? undefined,
              }))}
              valor={proveedorId}
              onCambiar={(id) => {
                setProveedorId(id)
                if (id !== null) setTipoProveedorId(null)
              }}
              vacio="Sin proveedor fijo"
              placeholder="Nombre o RIF…"
            />
          </Campo>
        )}
        <Campo para="gasto-divisa" etiqueta="Divisa">
          <SelectorBuscable
            id="gasto-divisa"
            opciones={(divisas.data ?? []).map((d) => ({
              valor: d.id,
              etiqueta: d.codigo,
              detalle: d.nombre,
            }))}
            valor={divisaId}
            onCambiar={setDivisaId}
            vacio="—"
          />
        </Campo>
      </div>
      <Campo
        para="gasto-monto"
        etiqueta="Monto de referencia"
        ayuda={
          tipoProveedorId !== null
            ? "El precio de quien no tenga uno propio. Necesita su divisa."
            : "Lo que suele costar. Necesita su divisa."
        }
      >
        <input
          id="gasto-monto"
          name="monto_referencia"
          inputMode="decimal"
          defaultValue={gasto?.monto_referencia ?? ""}
          className="campo mt-1 tabular-nums"
        />
      </Campo>

      {error && (
        <p className="text-sm" style={{ color: "var(--destructive)" }}>
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Boton onClick={onCerrar}>Cancelar</Boton>
        <Boton type="submit" variante="primario" disabled={guardar.isPending}>
          {guardar.isPending ? "Guardando…" : "Guardar"}
        </Boton>
      </div>
    </form>
  )
}

/**
 * Etiqueta, control y ayuda.
 *
 * `para` es el `id` del control: la etiqueta tiene que apuntar a él con `htmlFor`
 * —envolverlo no basta para un lector de pantalla, y Biome lo marca como error—.
 */
export function Campo({
  para,
  etiqueta,
  ayuda,
  children,
}: {
  para: string
  etiqueta: string
  ayuda?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium" htmlFor={para}>
        {etiqueta}
      </label>
      {children}
      {ayuda && (
        <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
          {ayuda}
        </span>
      )}
    </div>
  )
}
